import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { toast } from "sonner";
import { AHeader, ACard, ATable } from "@/components/shared/AdminKit";
import { Loader2, ShieldCheck, ShieldAlert, Ban, RotateCcw } from "lucide-react";

const STATUS = {
  active: "bg-emerald-500/15 text-emerald-400",
  suspended: "bg-amber-500/15 text-amber-400",
  inactive: "bg-slate-600/30 text-slate-300",
  blocked: "bg-red-500/15 text-red-400",
  disabled: "bg-red-500/15 text-red-400",
};

export default function AdminSecurity() {
  const { t: tr } = useContent();
  const [tab, setTab] = useState("accounts");
  const [accounts, setAccounts] = useState(null);
  const [logins, setLogins] = useState(null);

  const loadAcc = () => api.get("/admin/security/accounts").then((r) => setAccounts(r.data)).catch(() => setAccounts([]));
  useEffect(() => { loadAcc(); api.get("/admin/security/logins").then((r) => setLogins(r.data)).catch(() => setLogins([])); }, []);

  const setStatus = async (id, status) => {
    try { await api.post(`/admin/security/accounts/${id}/status`, { status }); toast.success(tr("sec.status_updated", "Statut mis à jour")); loadAcc(); }
    catch (e) { toast.error(e.response?.data?.detail || tr("sec.error", "Erreur")); }
  };

  return (
    <div>
      <AHeader title={tr("sec.title", "Accès & Sécurité")} subtitle={tr("sec.subtitle", "Comptes administrateurs/agents, historique des connexions et état 2FA")} />
      <div className="mb-4 mt-4 flex gap-2 border-b border-slate-800 text-sm">
        {[["accounts", tr("sec.tab.accounts", "Comptes & 2FA")], ["logins", tr("sec.tab.logins", "Historique des connexions")]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`security-tab-${k}`} className={`px-4 py-2 ${tab === k ? "border-b-2 border-accent text-accent" : "text-slate-400"}`}>{l}</button>
        ))}
      </div>

      {tab === "accounts" && (accounts === null ? <Loader /> : (
        <ACard title={`${tr("sec.accounts", "Comptes")} (${accounts.length})`}>
          <ATable head={[tr("sec.col.account", "Compte"), tr("sec.col.role", "Rôle"), tr("sec.col.status", "Statut"), tr("sec.col.2fa", "2FA"), tr("sec.col.last_login", "Dernière connexion"), tr("sec.col.actions", "Actions")]}>
            {accounts.map((a) => (
              <tr key={a.id} className="border-b border-slate-800/60" data-testid={`sec-account-${a.id}`}>
                <td className="px-3 py-2.5"><div className="font-medium text-white">{a.name}</div><div className="text-xs text-slate-500">{a.email}</div></td>
                <td className="px-3 py-2.5"><span className="rounded bg-slate-800 px-2 py-0.5 text-xs">{a.role === "agent" ? a.agent_role || "agent" : a.role}</span></td>
                <td className="px-3 py-2.5"><span className={`rounded px-2 py-0.5 text-xs ${STATUS[a.status] || ""}`}>{a.status}</span></td>
                <td className="px-3 py-2.5">{a.twofa ? <span className="flex items-center gap-1 text-xs text-emerald-400"><ShieldCheck className="h-3.5 w-3.5" /> {tr("sec.2fa.on", "Activée")}</span> : <span className="flex items-center gap-1 text-xs text-slate-500"><ShieldAlert className="h-3.5 w-3.5" /> {a.twofa_policy === "required" ? tr("sec.2fa.required", "Requise") : tr("sec.2fa.off", "Désactivée")}</span>}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{a.last_login ? new Date(a.last_login).toLocaleString("fr-FR") : "—"}</td>
                <td className="px-3 py-2.5">
                  {a.role === "super_admin" ? <span className="text-xs text-slate-600">{tr("sec.protected", "protégé")}</span> : (
                    <div className="flex gap-1">
                      {a.status === "active"
                        ? <>
                            <button onClick={() => setStatus(a.id, "suspended")} data-testid={`sec-suspend-${a.id}`} title={tr("sec.suspend", "Suspendre")} className="rounded p-1.5 text-amber-400 hover:bg-slate-800"><Ban className="h-4 w-4" /></button>
                            <button onClick={() => setStatus(a.id, "blocked")} data-testid={`sec-block-${a.id}`} title={tr("sec.block", "Bloquer")} className="rounded p-1.5 text-red-400 hover:bg-slate-800"><ShieldAlert className="h-4 w-4" /></button>
                          </>
                        : <button onClick={() => setStatus(a.id, "active")} data-testid={`sec-reactivate-${a.id}`} title={tr("sec.reactivate", "Réactiver")} className="rounded p-1.5 text-emerald-400 hover:bg-slate-800"><RotateCcw className="h-4 w-4" /></button>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </ATable>
        </ACard>
      ))}

      {tab === "logins" && (logins === null ? <Loader /> : (
        <ACard title={`${tr("sec.recent_logins", "Connexions récentes")} (${logins.length})`}>
          <ATable head={[tr("sec.col.event", "Événement"), tr("sec.col.email", "Email"), tr("sec.col.ip", "IP"), tr("sec.col.date", "Date")]}>
            {logins.map((l, i) => (
              <tr key={i} className="border-b border-slate-800/60" data-testid={`sec-login-${i}`}>
                <td className="px-3 py-2.5"><span className={`text-xs ${l.event === "login_success" ? "text-emerald-400" : l.event === "login_failure" ? "text-red-400" : "text-slate-300"}`}>{l.event}</span></td>
                <td className="px-3 py-2.5 text-slate-400">{l.email || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{l.ip || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{new Date(l.created_at).toLocaleString("fr-FR")}</td>
              </tr>
            ))}
          </ATable>
        </ACard>
      ))}
    </div>
  );
}

function Loader() { return <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>; }
